/**
 * Licence plates by country: how a plate is written and how the EU style plate
 * is drawn (country sign, national colours as a small mark between the groups).
 * Countries are ISO 3166 alpha-2 codes.
 */
import { normalizePlate, plateSearchKey } from "./masterData";

export interface PlateCountry {
  code: string;
  /** International distinguishing sign shown on the plate (H, SK, A, ...). */
  sign: string;
  /** Plate with the blue EU band and the ring of stars. */
  eu: boolean;
  /** Colours of the small national mark between the groups (top to bottom), if the plate has one. */
  mark?: readonly string[];
  /** Red stripes at the top and bottom (Austria). */
  stripes?: string;
  /** Band colours for non EU plates (top, bottom). */
  band?: readonly [string, string];
}

export const PLATE_COUNTRIES: readonly PlateCountry[] = [
  { code: "HU", sign: "H", eu: true, mark: ["#ce2939", "#ffffff", "#477050"] },
  { code: "SK", sign: "SK", eu: true, mark: ["#ffffff", "#0b4ea2", "#ee1c25"] },
  { code: "AT", sign: "A", eu: true, stripes: "#c8102e" },
  { code: "DE", sign: "D", eu: true },
  { code: "CZ", sign: "CZ", eu: true },
  { code: "PL", sign: "PL", eu: true },
  { code: "RO", sign: "RO", eu: true },
  { code: "HR", sign: "HR", eu: true, mark: ["#ff0000", "#ffffff", "#171796"] },
  { code: "SI", sign: "SLO", eu: true },
  { code: "RS", sign: "SRB", eu: false, band: ["#0c4076", "#0c4076"] },
  { code: "UA", sign: "UA", eu: false, band: ["#0057b7", "#ffd700"] },
];

export const PLATE_COUNTRY_CODES = PLATE_COUNTRIES.map((c) => c.code);

/** Any other country: the plate is drawn as a plain frame (XX is a user assigned ISO code). */
export const OTHER_COUNTRY = "XX";

export function plateCountry(code: string | null | undefined): PlateCountry | null {
  return PLATE_COUNTRIES.find((c) => c.code === code) ?? null;
}

/** The workshop's own country, from its language (used when a vehicle has none). */
export function defaultPlateCountry(language: string): string | null {
  const byLanguage: Record<string, string> = {
    hu: "HU", sk: "SK", cs: "CZ", pl: "PL", ro: "RO", hr: "HR", sl: "SI", "sr-Latn": "RS", uk: "UA", de: "DE",
  };
  return byLanguage[language] ?? null;
}

/**
 * Writes a plate the way it is written in its country when the format is known
 * (Hungarian ABC123 → ABC-123, AEDF123 → AE DF-123; Slovak BA123AB → BA-123AB),
 * otherwise upper case with single spaces as typed.
 */
export function formatPlate(plate: string, country: string | null | undefined): string {
  const key = plateSearchKey(plate);
  if (country === "HU") {
    if (/^[A-Z]{3}\d{3}$/.test(key)) return `${key.slice(0, 3)}-${key.slice(3)}`;
    if (/^[A-Z]{4}\d{3}$/.test(key)) return `${key.slice(0, 2)} ${key.slice(2, 4)}-${key.slice(4)}`;
  }
  if (country === "SK" && /^[A-Z]{2}\d{3}[A-Z]{2}$/.test(key)) return `${key.slice(0, 2)}-${key.slice(2)}`;
  return normalizePlate(plate);
}

/**
 * The groups drawn on the plate: [before the national mark, after it]. Plates
 * without a mark, or with an unknown format, are a single group.
 */
export function plateGroups(plate: string, country: string | null | undefined): [string] | [string, string] {
  const text = formatPlate(plate, country);
  const key = plateSearchKey(plate);
  if (country === "HU" && /^[A-Z]{4}\d{3}$/.test(key)) return [key.slice(0, 2), `${key.slice(2, 4)}-${key.slice(4)}`];
  if (country === "SK" && /^[A-Z]{2}\d{3}[A-Z]{2}$/.test(key)) return [key.slice(0, 2), key.slice(2)];
  if (country === "HR" && /^[A-Z]{2}\d{3,4}[A-Z]{1,2}$/.test(key)) {
    const m = /^([A-Z]{2})(\d{3,4})([A-Z]{1,2})$/.exec(key)!;
    return [m[1]!, `${m[2]}-${m[3]}`];
  }
  return [text];
}
